import { invalidateIntegrationQuery, usePostExternalIntegrationStartStageMutation } from '@Queries/index';
import { bs } from '@Styles/styles';
import * as Api from '@ViewModels';
import {
	EtlFailureKind,
	EtlState,
	IBaseEtlMetadata,
	IEtlMetadata,
	IExtractEtlMetadata,
	ILoadMetadata,
	IPreProcessEtlMetadata,
	ITransformEtlMetadata,
} from '@ViewModels';
import { DisclosureIcon } from '@WebComponents/svgs/icons/DisclosureIcon';
import { SuccessCheckIcon } from '@WebComponents/svgs/icons/SuccessCheckIcon';
import { css } from 'aphrodite';
import moment from 'moment';
import React from 'react';
import { toast } from 'sonner';
import etlExtract from '../../../../../../../web/assets/etl-extract.svg';
import etlLoad from '../../../../../../../web/assets/etl-load.svg';
import etlTransform from '../../../../../../../web/assets/etl-transform.svg';
import {
	Dropdown,
	DropdownContent,
	DropdownItem,
	DropdownTrigger,
} from '../../../../../../../web/components-v2/Dropdown';
import { Button } from '../../../../../../../web/components-v2/Button';
import { ETL_STATE_LABELS, EtlStateIcon } from '../EtlStateIcon';
import { ETLSubConnector } from '../ETLVisuals';
import { styleSheet } from './styles';

interface IETLStageProps {
	label: string;
	metadata: IBaseEtlMetadata;
	stage: keyof IEtlMetadata;
	provider: Api.IntegrationProvider;
	impersonationContext?: Api.IImpersonationContext;
	anyStageInProgress: boolean;
	isPipelineSuspended: boolean;
	preProcessMetadata?: Api.IPreProcessEtlMetadata;
}

export const ETLStage: React.FC<IETLStageProps> = ({
	label,
	metadata,
	stage,
	provider,
	impersonationContext,
	anyStageInProgress,
	isPipelineSuspended,
	preProcessMetadata,
}) => {
	const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

	const getDisplayDate = (date?: Date) => {
		if (!date) return 'N/A';
		return moment(date).format('MM/DD/YY h:mm A');
	};

	const refreshIntegration = () => {
		invalidateIntegrationQuery({
			impersonationContext,
			provider,
		});
	};

	const postExternalIntegrationStartStageMutation = usePostExternalIntegrationStartStageMutation({
		impersonationContext,
		onSuccess: () => refreshIntegration(),
	});

	const getStageIcon = () => {
		if (stage === 'extract') return etlExtract;
		if (stage === 'transform') return etlTransform;
		if (stage === 'load') return etlLoad;
	};
	const onStart = (forceAll = false) => {
		if (isPipelineSuspended) {
			toast.info(
				'The pipeline is currently suspended. Starting any stage of the ETL requires resolving the suspended state first.'
			);
			return;
		}

		postExternalIntegrationStartStageMutation.mutateAsync({
			etlStage: (stage[0].toUpperCase() + stage.slice(1)) as Api.EtlStage,
			integrationProvider: provider,
			forceAll,
		});
	};

	// Only the retry count lives here — it is escalation state that drives suspension and cannot be
	// reconstructed from the Recent Activity tab, where the failure history itself already lives.
	const getFailureDetail = ({ state, consecutiveTransientFailures, lastFailureKind }: IBaseEtlMetadata) => {
		if (state !== EtlState.IdleWithError && state !== EtlState.SuspendedWithError) return '';
		if (lastFailureKind !== EtlFailureKind.Transient || !consecutiveTransientFailures) return '';
		const noun = consecutiveTransientFailures === 1 ? 'failure' : 'failures';
		const outcome = state === EtlState.IdleWithError ? 'will retry automatically' : 'retries exhausted';
		return `${consecutiveTransientFailures} transient ${noun} · ${outcome}`;
	};

	const getStateLabel = (
		stageMetadata: IExtractEtlMetadata | ITransformEtlMetadata | ILoadMetadata | IPreProcessEtlMetadata
	) => {
		// The card has room for the trailing ellipsis that the compact pipeline node does not.
		if (stageMetadata.state === EtlState.InProgress) return 'Processing...';
		return ETL_STATE_LABELS[stageMetadata.state];
	};

	const isDisabled = anyStageInProgress;
	const failureDetail = getFailureDetail(metadata);
	const isErrored = metadata.state === EtlState.IdleWithError || metadata.state === EtlState.SuspendedWithError;

	const getAccentStyle = () => {
		if (metadata.state === EtlState.IdleWithError) return styleSheet.accentBarWarning;
		if (metadata.state === EtlState.SuspendedWithError) return styleSheet.accentBarDanger;
		return null;
	};

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.content)}>
				{/* Always rendered so the card body starts at the same offset whether or not the stage errored */}
				<span className={css(styleSheet.accentBar, getAccentStyle())} aria-hidden='true' />
				<div className={css(styleSheet.body)}>
					<div className={css(styleSheet.iconContainer)}>
						<img src={getStageIcon()} alt={label} />
					</div>
					<div className={css(styleSheet.textContainer)}>
						<h3 className={css(styleSheet.label)}>{label}</h3>
						<p className={css(styleSheet.subLabel, isErrored && styleSheet.subLabelError)}>
							<EtlStateIcon state={metadata.state} />
							{getStateLabel(metadata)}
						</p>
						{failureDetail ? <p className={css(styleSheet.failureDetail)}>{failureDetail}</p> : null}
						{stage === 'extract' && preProcessMetadata && (
							<div className={css(styleSheet.substep)}>
								<ETLSubConnector status={preProcessMetadata.state} />
								<div>
									<h3
										className={css(
											styleSheet.substepLabel,
											preProcessMetadata.state !== EtlState.InProgress && styleSheet.substepLabelIdle
										)}
									>
										Pre-processing
									</h3>
									<p className={css(styleSheet.substepStatus)}>{getStateLabel(preProcessMetadata)}</p>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
			<footer className={css(styleSheet.footer)}>
				<div className={css(styleSheet.dateContainer)}>
					{metadata.lastFinished ? <SuccessCheckIcon /> : null}
					{metadata.lastFinished ? 'Last Success: ' : 'Started Date: '}
					<span className={css(bs.fontBold)}>{getDisplayDate(metadata.lastFinished || metadata.lastStarted)}</span>
				</div>
				{stage === 'extract' || stage === 'transform' ? (
					<Dropdown open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
						<DropdownTrigger className={css(styleSheet.buttonStyleTrigger)} disabled={isDisabled}>
							<span className={css(styleSheet.triggerText)}>
								{metadata.state === EtlState.InProgress ? 'Running' : 'Start'}
							</span>
							<DisclosureIcon
								className={css(styleSheet.triggerChevron, isDropdownOpen && styleSheet.triggerChevronRotated)}
								fillColor='white'
							/>
						</DropdownTrigger>
						<DropdownContent styles={[styleSheet.compactDropdownContent]}>
							<DropdownItem onClick={() => onStart()} disabled={isDisabled}>
								From Last Run
							</DropdownItem>
							<DropdownItem onClick={() => onStart(true)} disabled={isDisabled}>
								{stage === 'extract' ? 'Full Extract' : 'Full Transform'}
							</DropdownItem>
						</DropdownContent>
					</Dropdown>
				) : (
					<Button
						variant='primary'
						size='sm'
						onClick={() => onStart()}
						disabled={isDisabled}
						styles={[styleSheet.button]}
					>
						{metadata.state === EtlState.InProgress ? 'Running' : 'Start'}
					</Button>
				)}
			</footer>
		</div>
	);
};
